/* eslint-disable promise/always-return */
/* eslint-disable promise/catch-or-return */
/* eslint-disable react/no-array-index-key */

import {
  Box,
  Button,
  Container,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Paper,
  CircularProgress,
  Chip,
  Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import {
  formatCurrency,
  formatList,
} from 'renderer/shared/formatters/formatters';
import { Link, useLocation } from 'react-router-dom';
import { DownloadOutlined } from '@mui/icons-material';
import { PDFDownloadLink } from '@react-pdf/renderer';
import ExportPDFView from 'renderer/features/users-page/components/export-pdf';
import { transformTransactionToInvoice } from 'renderer/features/payments-page/schemas/transaction.transformer';
import { TransactionDetails } from 'renderer/features/payments-page/schemas/transaction.schemas';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useState } from 'react';
import AuthorizeDialog from 'renderer/features/products-page/components/AuthorizeDialog';
import { useGoogleAds } from 'renderer/shared/analytics';
import CheckoutNotice from './CheckoutNotice';
import { PurchaseConfirmation } from '../schemas/purchase.schemas';
import { useCreatePaypalCaptureMutation } from '../services/purchase.endpoints';

const rootDiscountCode = (code?: string) =>
  (code || '').replace(/-apportioned.*$/, '');

/** Order-scope discounts show once on the order, not per line (matches cart). */
const isOrderScopeDiscountDetails = (
  discount?: {
    copyLimitScope?: string;
    code?: string;
  } | null,
) =>
  !!discount &&
  (discount.copyLimitScope === 'order' ||
    /-apportioned-/.test(discount.code || ''));

const getOrderScopeDiscountSummaries = (
  transactions: TransactionDetails[] | undefined,
) => {
  const byCode = new Map<string, number>();
  (transactions ?? []).forEach((txn) => {
    const discount = txn.prices?.discountDetails;
    if (
      !discount?.success ||
      !(discount.amount > 0) ||
      !isOrderScopeDiscountDetails(discount)
    ) {
      return;
    }
    const root = rootDiscountCode(discount.code) || 'order';
    byCode.set(root, (byCode.get(root) || 0) + discount.amount);
  });
  return [...byCode.entries()].map(([code, amount]) => ({ code, amount }));
};

const PAYPAL_HOLD_DEFAULT_MESSAGE =
  'Your purchase is processing in Paypal. Once it is complete you will receive a confirmation email with your serial number. Please contact cs@digitalanarchy.com if you have any questions about this order';

const isAllHostAppsGroup = (g: any) =>
  String(g.displayName || g.productName || '')
    .toLowerCase()
    .includes('all host');

const getAllHostUpgradeInfo = (txn: TransactionDetails) => {
  if (!Array.isArray(txn.serialGroups)) {
    return { isAllHostUpgrade: false };
  }

  const allHostGroup = txn.serialGroups.find(isAllHostAppsGroup);
  const perHostGroups = txn.serialGroups.filter((g) => !isAllHostAppsGroup(g));

  return {
    isAllHostUpgrade: Boolean(allHostGroup && perHostGroups.length),
    allHostGroup,
    perHostGroups,
  };
};

/* ────────────────────────────────────────────── */

const PDFIconButton = ({
  transaction,
  customerName,
}: {
  transaction: TransactionDetails;
  customerName: string;
}) => {
  const theme = useTheme();
  const transactionInfo = transformTransactionToInvoice(transaction);
  return (
    <PDFDownloadLink
      document={<ExportPDFView transaction={transactionInfo} />}
      fileName={`${transaction._id}.pdf`}
    >
      <Tooltip title="Download" placement="top">
        <IconButton>
          <DownloadOutlined style={{ color: theme.palette.grey[900] }} />
        </IconButton>
      </Tooltip>
    </PDFDownloadLink>
  );
};

const renderSerialInfo = (
  txn: TransactionDetails,
  fallbackSerials: string[] = [],
) => {
  if (Array.isArray(txn.serialGroups) && txn.serialGroups.length) {
    const { isAllHostUpgrade, perHostGroups } = getAllHostUpgradeInfo(txn);
    const visibleGroups = isAllHostUpgrade
      ? perHostGroups
      : txn.serialGroups ?? [];

    return (
      <Box component="ul" sx={{ pl: 3, m: 0 }}>
        {(visibleGroups ?? []).map((g, i) => (
          <Typography
            key={`${g.productId}-${i}`}
            component="li"
            variant="body2"
            sx={{ listStyleType: 'disc', color: 'text.secondary' }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 500,
                color: 'text.primary',
                textDecoration: 'underline',
              }}
            >
              {g.displayName ?? g.productName}
            </Box>
            :&nbsp;{formatList(g.serials)}
          </Typography>
        ))}
      </Box>
    );
  }

  return (
    <Typography variant="body2" color="textSecondary">
      Serials:&nbsp;{formatList(txn.serials ?? fallbackSerials)}
    </Typography>
  );
};

const resolveTransactionLineTotal = (txn: TransactionDetails): number => {
  const quantity = txn.quantity ?? 1;
  const { prices } = txn;
  if (!prices) return txn.amount ?? 0;

  // Order-scope: line shows list price; the discount only appears on the order.
  if (isOrderScopeDiscountDetails(prices.discountDetails)) {
    return (prices.startingPrice ?? txn.fullPrice ?? 0) * quantity;
  }

  const copiesInPrices = prices.copies ?? 1;
  if (quantity === 1 && copiesInPrices > 1) {
    if (typeof txn.amount === 'number' && txn.amount > 0) {
      return txn.amount;
    }
    return (prices.totalPrice ?? 0) / copiesInPrices;
  }

  return prices.totalPrice ?? txn.amount ?? 0;
};

/** What was actually charged for this transaction (post-discount). */
const resolveTransactionChargeTotal = (txn: TransactionDetails): number => {
  const quantity = txn.quantity ?? 1;
  const { prices } = txn;
  if (!prices) return txn.amount ?? 0;

  const copiesInPrices = prices.copies ?? 1;
  if (quantity === 1 && copiesInPrices > 1) {
    if (typeof txn.amount === 'number' && txn.amount > 0) {
      return txn.amount;
    }
    return (prices.totalPrice ?? 0) / copiesInPrices;
  }

  return prices.totalPrice ?? txn.amount ?? 0;
};

/* ────────────────────────────────────────────── */

const PurchaseConfirmationPage = (confirmation: PurchaseConfirmation) => {
  const {
    productName,
    prices,
    to,
    serialNumbers,
    transactions,
    customerName,
    transactionId,
    paypalCaptureHeld,
    confirmationMessage,
  } = confirmation;
  console.log('PurchaseConfirmationPage', { paypalCaptureHeld });
  useGoogleAds('purchase', confirmation);

  const [capturePaypalCheckout] = useCreatePaypalCaptureMutation();
  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [loading] = useState(false);
  const [paypalResult] = useState<any>(null);
  const [error] = useState<string | null>(null);

  const location = useLocation();
  const fallback = transactions?.[0];

  const displayName =
    customerName ??
    `${fallback?.firstName ?? ''} ${fallback?.lastName ?? ''}`.trim();
  const displayTo = to ?? fallback?.email;

  // Subtotal + total
  const computedSubtotal = (transactions ?? []).reduce((sum, txn) => {
    const unitPrice = txn.prices?.startingPrice ?? txn.fullPrice ?? 0;
    return sum + unitPrice * (txn.quantity ?? 1);
  }, 0);
  const computedTotal = (transactions ?? []).reduce((sum, txn) => {
    return sum + resolveTransactionChargeTotal(txn);
  }, 0);

  const showTotals = true;

  const activeTransactions = paypalResult
    ? [
        {
          _id: paypalResult.transactionId,
          productName: paypalResult.planId ?? productName ?? 'PayPal Purchase',
          prices: {
            startingPrice: Number(paypalResult.amount) || 0,
            totalPrice: Number(paypalResult.amount) || 0,
            discountDetails: null,
          },
          serials: [],
          quantity: 1,
        } as unknown as TransactionDetails,
      ]
    : transactions;

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Finalizing your PayPal purchase…
        </Typography>
      </Container>
    );
  }

  if (error) {
    return <CheckoutNotice title="Checkout not completed" message={error} />;
  }

  return (
    <Container maxWidth="md" sx={{ p: 0 }}>
      <Paper elevation={0} sx={{ textAlign: 'center', padding: 0 }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
        <Typography variant="h4" gutterBottom>
          Thank you for your purchase!
        </Typography>
        {paypalCaptureHeld ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: 2,
              mb: 1,
            }}
          >
            <Alert
              severity="warning"
              variant="outlined"
              icon={<ScheduleIcon fontSize="inherit" />}
              sx={{
                maxWidth: 640,
                width: '100%',
                textAlign: 'left',
                borderWidth: 2,
                bgcolor: '#fff8e1',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                PayPal payment processing
              </Typography>
              <Typography variant="body2" color="text.primary">
                {confirmationMessage ?? PAYPAL_HOLD_DEFAULT_MESSAGE}
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Typography variant="subtitle1" gutterBottom>
            A confirmation email has been sent to {displayTo}. Please save this
            for your records. If you created an account, your serial numbers and
            invoices are also available to you in your Account Dashboard.
          </Typography>
        )}

        {/* Help authorize — only when serials are available */}
        {!paypalCaptureHeld && (
          <Box
            sx={{
              mt: 3,
              mb: 2,
              px: 2,
              py: 1.5,
              backgroundColor: '#e0f2f1',
              borderRadius: 1,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
            }}
            onClick={() => setAuthDialogOpen(true)}
          >
            <InfoOutlinedIcon sx={{ fontSize: '1.3rem', color: '#00796b' }} />
            <Typography sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
              Need help authorizing a plugin or subscription? Click here for
              instructions.
            </Typography>
          </Box>
        )}

        {/* Transactions table */}
        <TableContainer sx={{ mt: 4 }}>
          <Table size="small">
            <TableBody>
              {(activeTransactions ?? []).map((txn) => {
                const totalPrice = resolveTransactionLineTotal(txn);
                const discount = txn.prices?.discountDetails;

                return (
                  <TableRow key={txn._id}>
                    <TableCell sx={{ borderBottom: 'none' }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1" fontWeight="bold">
                          {txn.productName ?? productName}
                          {txn.quantity ? ` x ${txn.quantity}` : ''}
                        </Typography>

                        {getAllHostUpgradeInfo(txn).isAllHostUpgrade && (
                          <Chip
                            size="small"
                            label="All Host Apps"
                            color="primary"
                            variant="outlined"
                            sx={{ ml: 1 }}
                          />
                        )}
                      </Box>

                      {!paypalCaptureHeld &&
                        renderSerialInfo(txn, serialNumbers)}
                      {!paypalCaptureHeld &&
                        discount?.success &&
                        discount.amount > 0 &&
                        !isOrderScopeDiscountDetails(discount) && (
                          <Typography
                            variant="body2"
                            color="success.main"
                            mt={0.5}
                          >
                            You saved {formatCurrency(discount.amount)}
                            {discount.code &&
                              ` using code "${rootDiscountCode(
                                discount.code,
                              )}"`}
                          </Typography>
                        )}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ whiteSpace: 'nowrap', borderBottom: 'none' }}
                    >
                      {formatCurrency(totalPrice)}
                    </TableCell>
                  </TableRow>
                );
              })}
              <br />
              {showTotals && (
                <>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        Subtotal
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(computedSubtotal)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Box
                        display="flex"
                        alignItems="baseline"
                        gap={1}
                        flexWrap="wrap"
                      >
                        <Typography variant="body2" fontWeight="medium">
                          Discount
                        </Typography>
                        {getOrderScopeDiscountSummaries(transactions).map(
                          ({ code, amount }) => (
                            <Typography
                              key={code}
                              variant="body2"
                              color="success.main"
                            >
                              {formatCurrency(amount)} off order
                              {code !== 'order' ? ` ("${code}")` : ''}
                            </Typography>
                          ),
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      -
                      {formatCurrency(
                        (transactions ?? []).reduce((sum, txn) => {
                          return (
                            sum + (txn.prices?.discountDetails?.amount ?? 0)
                          );
                        }, 0),
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        Total
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(computedTotal)}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box mt={4}>
          <Link to="/">
            <Button variant="contained" color="primary">
              Go to Store
            </Button>
          </Link>
        </Box>
      </Paper>
      {!paypalCaptureHeld && (
        <AuthorizeDialog
          open={authDialogOpen}
          onClose={() => setAuthDialogOpen(false)}
        />
      )}
    </Container>
  );
};

export default PurchaseConfirmationPage;
